import { useTranslation } from "react-i18next";
import type { PublicProvider } from "../../api/publicProviders";
import { CountUp } from "./CountUp";
import { useReveal, type RevealState } from "./useReveal";
import styles from "./FeatureCards.module.css";

// Brand colours (STYLES.md) for the tools in an illustration, in order.
const toolColours = ["#e60054", "#8353fd", "#66c9ff", "#ffc700", "#ff592c", "#870247"];

/** What each illustration is drawn from. */
interface VisualProps {
  state: RevealState;
  providers: PublicProvider[] | null;
}

/** A month's spend as a ring, one segment a tool (up to four), filling towards the allowance. */
function AllowanceVisual({ state, providers }: VisualProps): React.JSX.Element {
  const { t } = useTranslation();
  const shares = [34, 20, 12, 6];
  const segments = (providers ?? []).slice(0, shares.length).map((provider, position) => ({
    tool: provider.vendorId,
    name: provider.displayName,
    share: shares[position],
    colour: toolColours[position],
  }));
  const used = segments.reduce((total, segment) => total + segment.share, 0);
  let start = 0;

  return (
    <div className={styles.allowance}>
      <div className={styles.ring}>
        <svg viewBox="0 0 120 120" aria-hidden="true">
          <circle cx="60" cy="60" r="48" pathLength={100} className={styles.ringTrack} />
          {segments.map((segment, position) => {
            const offset = -start;
            start += segment.share;
            return (
              <circle
                key={segment.tool}
                cx="60"
                cy="60"
                r="48"
                pathLength={100}
                className={styles.ringSegment}
                stroke={segment.colour}
                strokeDasharray={`${String(segment.share - 1)} ${String(101 - segment.share)}`}
                strokeDashoffset={offset}
                style={{ animationDelay: `${String(0.3 + position * 0.35)}s` }}
              />
            );
          })}
        </svg>
        <span className={styles.ringValue}>
          <CountUp from={0} to={used} state={state} durationMs={1300} delayMs={300} suffix="%" />
        </span>
      </div>
      <ul className={styles.legend}>
        {segments.map((segment) => (
          <li key={segment.tool}>
            <span className={styles.swatch} style={{ background: segment.colour }} />
            {segment.name}
          </li>
        ))}
        <li className={styles.legendNote}>{t("signedOut.features.monthlyPicture.visual")}</li>
      </ul>
    </div>
  );
}

/**
 * Spend so far this month, climbing, with the projection to month end carrying on past the allowance and the day it could run out
 * marked: the shape of My Usage's forecast chart.
 */
function ForecastVisual(): React.JSX.Element {
  const { t } = useTranslation();
  const daily = [2, 4, 3, 5, 3, 1, 1, 4, 5, 3, 4, 4, 1, 1, 5, 4, 3, 4];
  const actual = daily.reduce<number[]>((sums, spend) => [...sums, (sums.at(-1) ?? 0) + spend], []);
  const perDay = actual[actual.length - 1] / actual.length;
  const ceiling = 80;
  const lastDay = 30;
  const x = (day: number): number => 8 + day * 7;
  const y = (value: number): number => 76 - value * 0.6;
  const today = actual.length - 1;
  const runsOut = today + (ceiling - actual[today]) / perDay;
  const line = actual.map((value, day) => `${day === 0 ? "M" : "L"}${String(x(day))} ${String(y(value))}`).join(" ");
  const projected = actual[today] + perDay * (lastDay - today);

  return (
    <div className={styles.trend}>
      <svg viewBox="0 0 232 80" aria-hidden="true">
        <defs>
          <linearGradient id="signed-out-trend-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#8353fd" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#8353fd" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="signed-out-trend-line" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#66c9ff" />
            <stop offset="100%" stopColor="#8353fd" />
          </linearGradient>
        </defs>
        <line x1="0" x2="232" y1={y(ceiling)} y2={y(ceiling)} className={styles.ceiling} />
        <text x="4" y={y(ceiling) - 5} className={styles.ceilingLabel}>
          {t("signedOut.features.trackUsage.ceiling")}
        </text>
        <path d={`${line} L${String(x(today))} 80 L${String(x(0))} 80 Z`} className={styles.area} />
        <path d={line} pathLength={1} className={styles.line} />
        <path d={`M${String(x(today))} ${String(y(actual[today]))} L${String(x(lastDay))} ${String(y(projected))}`} className={styles.projection} />
        <line x1={x(today)} x2={x(today)} y1={y(actual[today])} y2="78" className={styles.today} />
        <circle cx={x(runsOut)} cy={y(ceiling)} r="10" className={styles.spikeRing} />
        <circle cx={x(runsOut)} cy={y(ceiling)} r="4.5" className={styles.spikeDot} />
      </svg>
      <span className={styles.flag}>{t("signedOut.features.trackUsage.visual")}</span>
    </div>
  );
}

/**
 * My Tools in miniature, from the real tools and how each charges: a metered tool has a monthly limit, a flat-fee tool a fixed fee and
 * no limit. Allowance moves from the first metered tool to the one being switched on (the last metered tool, or a flat-fee tool when
 * there is only one metered), and the change waits for an approver.
 */
function AllocationVisual({ state, providers }: VisualProps): React.JSX.Element {
  const { t } = useTranslation();
  const shown = (providers ?? []).slice(0, 4);
  const metered = shown.filter((provider) => provider.costModel === "Metered");
  const switchedOn = metered.length > 1 ? metered[metered.length - 1] : shown.find((provider) => provider.costModel === "FlatFee");
  const moved = switchedOn?.costModel === "Metered" ? 15 : 0;
  const allowance = 80;
  const rows = shown.map((provider, position) => {
    const flatFee = provider.costModel === "FlatFee";
    const from = flatFee ? 0 : provider === metered[0] ? 60 : provider === switchedOn ? 0 : 20;
    const to = provider === metered[0] ? from - moved : provider === switchedOn && !flatFee ? moved : from;
    return { tool: provider.vendorId, name: provider.displayName, colour: toolColours[position], flatFee, switchedOn: provider === switchedOn, from, to };
  });

  return (
    <div className={styles.allocation}>
      <ul className={styles.rows}>
        {rows.map((row) => (
          <li key={row.tool} className={styles.row}>
            <span className={styles.rowName}>
              {row.switchedOn && <span className={styles.toggle} />}
              {row.name}
            </span>
            <span className={styles.bar}>
              <span
                className={styles.barFill}
                data-moves={row.from !== row.to}
                data-flat-fee={row.flatFee}
                style={{ width: `${String(((row.flatFee ? allowance : row.to) / allowance) * 100)}%`, "--bar-from": `${String((row.from / allowance) * 100)}%`, background: row.flatFee ? undefined : row.colour } as React.CSSProperties}
              />
            </span>
            <span className={styles.rowAmount}>
              {row.flatFee ? t("signedOut.features.requestAccess.flatFee") : <CountUp from={row.from} to={row.to} state={state} currency="USD" delayMs={600} durationMs={900} />}
            </span>
          </li>
        ))}
      </ul>
      <span className={styles.pending}>{t("signedOut.features.requestAccess.visual")}</span>
    </div>
  );
}

const features = [
  { key: "monthlyPicture", Visual: AllowanceVisual },
  { key: "trackUsage", Visual: ForecastVisual },
  { key: "requestAccess", Visual: AllocationVisual },
] as const;

/**
 * What a signed-in engineer can do, as three cards whose illustrations play out (a spend ring filling, a forecast reaching the
 * allowance, allowance moving to a newly switched-on tool) as they scroll into view. Each one shows something the portal really does, and
 * the tools in them are the portal's own. The cards tilt towards the pointer. The illustrations are decorative: each card's heading and
 * text carry its meaning.
 *
 * @param props.animate - Whether the page's animation is on; the cards are shown still, in their final state, when it is not.
 * @param props.providers - The AI tools the portal covers, or null when not known.
 * @param props.tools - The same tools as one phrase, for the text.
 */
export function FeatureCards({ animate, providers, tools }: { animate: boolean; providers: PublicProvider[] | null; tools: string }): React.JSX.Element {
  const { t } = useTranslation();
  const [ref, state] = useReveal<HTMLDivElement>(animate);

  return (
    <div ref={ref} className={styles.grid} data-state={state}>
      {features.map(({ key, Visual }, position) => (
        <article
          key={key}
          className={styles.card}
          style={{ animationDelay: `${String(position * 0.15)}s` }}
          onPointerMove={(event) => {
            const bounds = event.currentTarget.getBoundingClientRect();
            const across = (event.clientX - bounds.left) / bounds.width - 0.5;
            const down = (event.clientY - bounds.top) / bounds.height - 0.5;
            event.currentTarget.style.setProperty("--tilt-x", `${(across * 8).toFixed(2)}deg`);
            event.currentTarget.style.setProperty("--tilt-y", `${(down * -8).toFixed(2)}deg`);
            event.currentTarget.style.setProperty("--spot-x", `${String(event.clientX - bounds.left)}px`);
            event.currentTarget.style.setProperty("--spot-y", `${String(event.clientY - bounds.top)}px`);
          }}
          onPointerLeave={(event) => {
            event.currentTarget.style.removeProperty("--tilt-x");
            event.currentTarget.style.removeProperty("--tilt-y");
          }}
        >
          <div className={styles.stage} aria-hidden="true">
            <Visual state={state} providers={providers} />
          </div>
          <div className={styles.body}>
            <span className={styles.number} aria-hidden="true">
              {String(position + 1).padStart(2, "0")}
            </span>
            <h3>{t(`signedOut.features.${key}.title`)}</h3>
            <p>{t(`signedOut.features.${key}.description`, { tools })}</p>
          </div>
        </article>
      ))}
    </div>
  );
}
